import React from 'react';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell,
} from 'recharts';

/**
 * Componente reutilizable para gráficos en reportes
 * Soporta: bar, line, donut (pie chart)
 */
const ReportChart = ({ type = 'bar', data = [], config = {}, height = 300, valueFormatter }) => {
  if (!data || data.length === 0) {
    return (
      <div className="flex items-center justify-center h-full text-gray-500 text-sm">
        No hay datos disponibles
      </div>
    );
  }

  const primaryColor = config?.primaryColor || '#0F31F7';
  const secondaryColor = config?.secondaryColor || '#34495E';

  const COLORS = [primaryColor, secondaryColor, '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#06B6D4'];

  const defaultFormatter = (value) => {
    if (typeof value === 'number') {
      return value;
    }
    return value ?? '';
  };

  const formatter = valueFormatter || defaultFormatter;

  const customTooltip = {
    contentStyle: {
      backgroundColor: 'rgba(255, 255, 255, 0.95)',
      borderRadius: '8px',
      border: '1px solid #E5E7EB',
      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
    },
    labelStyle: {
      color: '#000',
      fontWeight: 'bold',
    },
  };

  // Gráfico de barras
  if (type === 'bar') {
    return (
      <ResponsiveContainer width="100%" height={height}>
        <BarChart
          data={data}
          margin={{ top: 20, right: 30, left: 0, bottom: 60 }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
          <XAxis
            dataKey="name"
            stroke="#6B7280"
            angle={-45}
            textAnchor="end"
            height={80}
            interval={0}
            tick={{ fontSize: 12 }}
          />
          <YAxis stroke="#6B7280" tick={{ fontSize: 12 }} />
          <Tooltip {...customTooltip} formatter={(value) => formatter(value)} />
          <Legend wrapperStyle={{ paddingTop: '20px' }} />
          {/* Si hay múltiples datasets, renderizar varios bars */}
          {data[0]?.value !== undefined ? (
            <Bar dataKey="value" fill={primaryColor} radius={[8, 8, 0, 0]} />
          ) : (
            // Detectar automáticamente qué campos renderizar
            Object.keys(data[0] || {})
              .filter((key) => key !== 'name' && typeof data[0][key] === 'number')
              .map((key, idx) => (
                <Bar
                  key={key}
                  dataKey={key}
                  fill={COLORS[idx % COLORS.length]}
                  radius={[8, 8, 0, 0]}
                />
              ))
          )}
        </BarChart>
      </ResponsiveContainer>
    );
  }

  // Gráfico de líneas
  if (type === 'line') {
    return (
      <ResponsiveContainer width="100%" height={height}>
        <LineChart
          data={data}
          margin={{ top: 20, right: 30, left: 0, bottom: 60 }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
          <XAxis
            dataKey="name"
            stroke="#6B7280"
            angle={-45}
            textAnchor="end"
            height={80}
            interval={Math.ceil(data.length / 8) - 1}
            tick={{ fontSize: 12 }}
          />
          <YAxis stroke="#6B7280" tick={{ fontSize: 12 }} />
          <Tooltip {...customTooltip} formatter={(value) => formatter(value)} />
          <Legend wrapperStyle={{ paddingTop: '20px' }} />
          {data[0]?.value !== undefined ? (
            <Line
              type="monotone"
              dataKey="value"
              stroke={primaryColor}
              strokeWidth={3}
              dot={{ fill: primaryColor, r: 4 }}
              activeDot={{ r: 6 }}
            />
          ) : (
            // Detectar automáticamente qué campos renderizar
            Object.keys(data[0] || {})
              .filter((key) => key !== 'name' && typeof data[0][key] === 'number')
              .map((key, idx) => (
                <Line
                  key={key}
                  type="monotone"
                  dataKey={key}
                  stroke={COLORS[idx % COLORS.length]}
                  strokeWidth={2}
                  dot={{ r: 3 }}
                  activeDot={{ r: 5 }}
                />
              ))
          )}
        </LineChart>
      </ResponsiveContainer>
    );
  }

  // Gráfico de dona (pie chart)
  if (type === 'donut' || type === 'pie') {
    return (
      <ResponsiveContainer width="100%" height={height}>
        <PieChart margin={{ top: 20, right: 30, left: 0, bottom: 20 }}>
          <Pie
            data={data}
            dataKey="value"
            nameKey="name"
            cx="50%"
            cy="50%"
            innerRadius={type === 'donut' ? 60 : 0}
            outerRadius={100}
            paddingAngle={2}
            label={({ value }) => formatter(value)}
          >
            {data.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
            ))}
          </Pie>
          <Tooltip {...customTooltip} formatter={(value) => formatter(value)} />
          <Legend wrapperStyle={{ paddingTop: '20px' }} />
        </PieChart>
      </ResponsiveContainer>
    );
  }

  // Tipo desconocido
  return (
    <div className="flex items-center justify-center h-full text-red-500 text-sm">
      Tipo de gráfico no soportado: {type}
    </div>
  );
};

export default ReportChart;
